import React, { useMemo, useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import Navbar from "@/components/Navbar";
import {
	Menu,
	Typography,
	Card,
	Descriptions,
	Tag,
	Space,
	Form,
	Input,
	Button,
	Tabs,
	Table,
	Select,
	DatePicker,
	Message,
	Link,
} from "@arco-design/web-react";
import { IconFile, IconBug } from "@arco-design/web-react/icon";
import { ApiDebugList } from "@/config/data";
import { getUserKeys } from "@/api";
import "./style.less";

const { Title, Text, Paragraph } = Typography;
const { TextArea } = Input;
const MenuItem = Menu.Item;
const SubMenu = Menu.SubMenu;

const ApiDebugPage: React.FC = () => {
	// 默认选中第一个接口
	const [selectedKey, setSelectedKey] = useState<string>(
		ApiDebugList?.[0]?.data?.[0]?.key || ""
	);
	// 分组键
	const groupKeys = useMemo(() => ApiDebugList.map((g) => g.key), []);
	// 所有接口
	const flatApis = useMemo(() => ApiDebugList.flatMap((g) => g.data || []), []);
	const [form] = Form.useForm();
	const [sending, setSending] = useState(false);
	const [resultText, setResultText] = useState<string>("");
	const [statusCode, setStatusCode] = useState<number | null>(null);
	const [useKeys, setUseKeys] = useState<string[]>([]);

	const history = useHistory();

	// 获取useKeys并动态设置表单默认值
	useEffect(() => {
		getUserKeys().then((res) => {
			const keys = (res.data as any[])
				.map((item) => (item as any).key as string)
				.filter((key) => key);

			setUseKeys(keys);

			// 🔑 关键：数据获取后立即设置表单默认值
			if (keys.length > 0) {
				form.setFieldsValue({
					"headers.Authorization": keys[0], // 设置Authorization字段的默认值
				});
			}
		});
	}, [form, selectedKey]);

	// 选中接口
	const selectedApi = useMemo(
		() => flatApis.find((i) => i.key === selectedKey),
		[flatApis, selectedKey]
	);

	// 获取请求方法颜色
	const getMethodColor = (method?: string) => {
		switch ((method || "").toUpperCase()) {
			case "GET":
				return "green";
			case "POST":
				return "blue";
			case "PUT":
				return "orange";
			case "DELETE":
				return "red";
			default:
				return "arcoblue";
		}
	};

	// 发送请求回调
	const handleSend = async () => {
		if (!selectedApi) return;
		try {
			setSending(true);
			setResultText("");
			setStatusCode(null);

			const values = form.getFieldsValue() || {};
			const headersInput = values.headers || {};
			const bodyInput = values.body || {};

			// 规范化 headers（DatePicker/Select/TextArea/输入框）
			const headers: Record<string, string> = {};
			Object.keys(headersInput).forEach((k) => {
				const v = headersInput[k];
				if (v === undefined || v === null || v === "") return;
				// @ts-ignore - DatePicker 的 dayjs
				if (v && typeof v === "object" && typeof v.format === "function") {
					// 中国时间格式 YYYY-MM-DD
					// @ts-ignore
					headers[k] = v.format("YYYY-MM-DD");
				} else {
					headers[k] = String(v);
				}
			});

			// 规范化 body - 处理数组字符串和布尔值字符串
			const body: Record<string, any> = {};
			Object.keys(bodyInput).forEach((k) => {
				const v = bodyInput[k];
				if (v === undefined || v === null || v === "") return;

				// @ts-ignore - DatePicker 的 dayjs
				if (v && typeof v === "object" && typeof v.format === "function") {
					// 中国时间格式 YYYY-MM-DD
					// @ts-ignore
					body[k] = v.format("YYYY-MM-DD");
				} else if (typeof v === "string") {
					// 尝试解析数组字符串
					if (v.startsWith("[") && v.endsWith("]")) {
						try {
							body[k] = JSON.parse(v);
						} catch {
							body[k] = v; // 解析失败则保持原字符串
						}
					}
					// 尝试解析布尔值字符串
					else if (v === "true" || v === "false") {
						body[k] = v === "true";
					}
					// 尝试解析数字字符串
					else if (!isNaN(Number(v)) && v !== "") {
						body[k] = Number(v);
					}
					// 其他情况保持原字符串
					else {
						body[k] = v;
					}
				} else {
					body[k] = v;
				}
			});

			// 拼接完整 URL：domain + endpoint
			const base = (selectedApi.domain || "").replace(/\/$/, "");
			const ep = selectedApi.endpoint || "";
			const fullPath = base + (ep.startsWith("/") ? ep : `/${ep}`);
			const method = (selectedApi.method || "GET").toUpperCase();

			let url = fullPath;
			let fetchBody: BodyInit | undefined = undefined;

			if (method === "GET" || method === "DELETE") {
				const qs = new URLSearchParams();
				Object.entries(body).forEach(([k, v]) => {
					if (Array.isArray(v)) {
						v.forEach((item) => qs.append(k, String(item)));
					} else {
						qs.append(k, String(v));
					}
				});
				const query = qs.toString();
				url = query ? `${fullPath}?${query}` : fullPath;
			} else {
				if (
					!Object.keys(headers).some((k) => k.toLowerCase() === "content-type")
				) {
					headers["Content-Type"] = "application/json";
				}
				fetchBody = JSON.stringify(body);
			}

			const resp = await fetch(url, {
				method,
				headers,
				body: fetchBody,
			});

			const text = await resp.text();
			setStatusCode(resp.status);
			try {
				const json = JSON.parse(text);
				setResultText(JSON.stringify(json, null, 2));
			} catch {
				setResultText(text);
			}
			if (!resp.ok) {
				Message.error(`请求失败：HTTP ${resp.status}`);
			}
		} catch (e: any) {
			Message.error(e?.message || "请求出错");
			setResultText(String(e?.message || e));
			setStatusCode(null);
		} finally {
			setSending(false);
		}
	};

	// 重置按钮回调
	const handleReset = () => {
		form.resetFields();
		setResultText("");
		setStatusCode(null);
	};

	// Tabs切换回调
	const handleMenuItemChange = (key: string) => {
		form.resetFields();
		setResultText("");
		setStatusCode(null);
		setSending(false);
	};

	// 跳转登录
	const handleLoginClick = () => {
		history.push("/login");
	};

	// 封装字段选择区域模块
	const RenderFieldElement = ({
		type,
		data,
	}: {
		type: "headers" | "body";
		data: any[];
	}) => {
		return (
			<React.Fragment>
				{data.map((item, i) => (
					<Form.Item
						label={`${item.key}（${item.required ? "必填" : "选填"}）`}
						key={i}
						rules={[{ required: item.required }]}
						field={`${type}.${item.key}`}
					>
						{item.value == "date" ? ( // 时间选择器
							<DatePicker
								style={{ width: "100%" }}
								disabledDate={(current) => current.isBefore(Date.now())}
							/>
						) : item.value == "textArea" ? ( // 文本域
							<TextArea placeholder={item.description} autoSize />
						) : item.key === "Authorization" && !useKeys.length ? (
							<Link status="warning" onClick={() => history.push("/console")}>
								当前暂无密钥，点击尝试创建一个新密钥
							</Link>
						) : (
							// 下拉选择框
							<Select
								options={
									item.key === "Authorization" && useKeys.length
										? useKeys
										: item.value
								}
								placeholder={item.description}
								style={{ flex: 1 }}
							/>
						)}
					</Form.Item>
				))}
			</React.Fragment>
		);
	};

	return (
		<div className="interface-debug-container" style={{ padding: "24px" }}>
			<div className="interface-debug-header">
				<Navbar onLoginClick={handleLoginClick} />
			</div>
			<div className="interface-debug-layout">
				<div className="interface-debug-layout-left">
					<Menu
						style={{ width: "100%", height: "100%" }}
						hasCollapseButton
						// openKeys={groupKeys}
						defaultOpenKeys={[ApiDebugList[0].key]}
						selectedKeys={[selectedKey]}
						onClickMenuItem={(k) => setSelectedKey(k)}
					>
						{ApiDebugList.map((group) => (
							<SubMenu
								key={group.key}
								title={
									<>
										<IconFile /> {group.name}
									</>
								}
							>
								{(group.data || []).map((api) => (
									<MenuItem onClick={handleMenuItemChange} key={api.key}>
										{api.name}
									</MenuItem>
								))}
							</SubMenu>
						))}
					</Menu>
				</div>

				<div className="interface-debug-layout-right">
					{selectedApi ? (
						<Space direction="vertical" size={16} style={{ width: "100%" }}>
							{/* 基本信息 */}
							<Card
								bordered
								title={
									<Space className={"interface-debug-card-title"} size={8}>
										<span
											className="sider-logo-box"
											style={{ display: "inline-flex" }}
										>
											<IconBug />
										</span>
										<Title heading={5} style={{ margin: 0 }}>
											{selectedApi.name}
										</Title>
										{/* <Tag color={getMethodColor(selectedApi.method)}>
											{selectedApi.method}
										</Tag> */}
									</Space>
								}
							>
								<Descriptions
									size="large"
									column={2}
									data={[
										{
											label: "请求方法",
											value: (
												<Tag color={getMethodColor(selectedApi.method)}>
													{selectedApi.method}
												</Tag>
											),
										},
										{
											label: "域名",
											value: <Text code>{selectedApi.domain}</Text>,
										},
										{
											label: "分组",
											value: (
												<Text>
													{ApiDebugList.find((g) =>
														(g.data || []).some(
															(i) => i.key === selectedApi.key
														)
													)?.name || "-"}
												</Text>
											),
										},
										{
											label: "接口路径",
											value: <Text code>{selectedApi.endpoint}</Text>,
										},
									]}
								/>
								<div style={{ marginTop: 16 }}>
									<Text style={{ display: "block", marginBottom: 8 }}>
										接口描述：
									</Text>
									<div className="description-content">
										<Paragraph style={{ margin: 0 }}>
											{selectedApi.description || "暂无描述"}
										</Paragraph>
									</div>
								</div>
							</Card>

							{/* 请求与响应 */}
							<Tabs
								className={"interface-debug-tabs"}
								defaultActiveTab="request"
								type="line"
							>
								<Tabs.TabPane key="request" title="请求">
									<Card bordered>
										<Space
											direction="vertical"
											style={{ width: "100%" }}
											size={16}
										>
											<Form
												layout="vertical"
												className="interface-debug-form"
												form={form}
											>
												<Title
													heading={6}
													style={{ margin: 0, marginBottom: 15 }}
												>
													请求头 (Headers)
												</Title>
												<RenderFieldElement
													type="headers"
													data={selectedApi.requestHeaders}
												/>

												<Title
													heading={6}
													style={{ margin: 0, marginBottom: 15 }}
												>
													请求体 (Body)
												</Title>
												<RenderFieldElement
													type="body"
													data={selectedApi.requestBody}
												/>

												<Title
													heading={6}
													style={{ margin: 0, marginBottom: 15 }}
												>
													调用结果
													{typeof statusCode === "number"
														? `（HTTP ${statusCode}）`
														: ""}
												</Title>
												<div className="response-result-code-block">
													<pre>
														<code>{resultText || "// 暂无结果"}</code>
													</pre>
												</div>
											</Form>

											<Space size="large" className="interface-debug-form-btns">
												<Button
													status="default"
													onClick={handleReset}
													disabled={sending}
												>
													重置
												</Button>
												<Button
													type="primary"
													onClick={handleSend}
													loading={sending}
												>
													发送请求
												</Button>
											</Space>
										</Space>
									</Card>
								</Tabs.TabPane>

								<Tabs.TabPane key="response" title="响应">
									<Card bordered>
										<Space
											direction="vertical"
											style={{ width: "100%" }}
											size={16}
										>
											<Title heading={6} style={{ margin: 0 }}>
												响应示例
											</Title>
											<div className="response-result-code-block">
												<pre>
													<code style={{ whiteSpace: "pre-wrap" }}>
														{JSON.stringify(
															selectedApi.responseExample,
															null,
															2
														)}
													</code>
												</pre>
											</div>

											<Title heading={6} style={{ margin: 0 }}>
												字段说明
											</Title>
											<Table
												size="small"
												pagination={false}
												columns={selectedApi.fieldDescription.columns}
												data={selectedApi.fieldDescription.data}
											/>

											<Title heading={6} style={{ margin: 0 }}>
												响应状态
											</Title>
											<Table
												size="small"
												pagination={false}
												columns={selectedApi.responseStatus.columns}
												data={selectedApi.responseStatus.data}
											/>
										</Space>
									</Card>
								</Tabs.TabPane>

								<Tabs.TabPane key="sdkExample" title="SDK 示例">
									<Card bordered>
										<Space
											direction="vertical"
											style={{ width: "100%" }}
											size={16}
										>
											<Form layout="vertical" className="interface-debug-form">
												{selectedApi.SDKExample.map((item, i) => {
													return (
														<div key={i}>
															<Title
																heading={6}
																style={{ margin: 0, marginBottom: 15 }}
															>
																{item.name}
															</Title>
															<div className="response-result-code-block">
																<pre>
																	<code>{item.code}</code>
																</pre>
															</div>
														</div>
													);
												})}
											</Form>
										</Space>
									</Card>
								</Tabs.TabPane>

								<Tabs.TabPane key="precautions" title="注意事项">
									<Card bordered>
										<Space
											direction="vertical"
											style={{ width: "100%" }}
											size={16}
										>
											<Form layout="vertical" className="interface-debug-form">
												<ul className="interface-debug-precautions-list">
													{selectedApi.precautions.map((item, i) => {
														return <li key={i}>{item}</li>;
													})}
												</ul>
											</Form>
										</Space>
									</Card>
								</Tabs.TabPane>
							</Tabs>
						</Space>
					) : (
						<Card bordered>
							<Space
								direction="vertical"
								align="center"
								style={{ width: "100%" }}
							>
								<div className="empty-icon-box" style={{ marginTop: 24 }}>
									<IconBug />
								</div>
								<Title heading={5} style={{ margin: 0 }}>
									请选择左侧一个接口
								</Title>
								<Text>选择后将在此展示接口详情、请求示例与响应示例</Text>
							</Space>
						</Card>
					)}
				</div>
			</div>
		</div>
	);
};

export default ApiDebugPage;
